import styles from './AddOnServices.module.scss';
import { Option } from '@spike/model';
import useNonInitialEffect from '@versiondos/hooks';
import { fetchThunk, deleteThunk, changeStateThunk, AddOnServicesStatus } from '@spike/addon-services-action';
import { Button, ConfirmDialog, OverFullWindow, Spinner } from 'components/UI';
import AddOnService, { createEmptyAddOnService } from '@spike/addon-service-model';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import AddOnServiceForm from './AddOnServiceForm';
import ServicesAddonsEmpty from './Empty';
import ServicesAddonsHeader from './Header';
import Table from './Table';
import Filters from './Table/Filters';
import Search from './Table/Search';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuid } from 'uuid';
import { useApiClientWrapper } from 'hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';

const ActiveFilterOption: Option<string> = {
    id: 'active',
    name: 'Active'
};

const InactiveFilterOption: Option<string> = {
    id: 'inactive',
    name: 'Inactive'
};

const AllFilterOption: Option<string> = {
    id: 'all',
    name: 'All'
};

const FilterOptions: Array<Option<string>> = [ActiveFilterOption, InactiveFilterOption, AllFilterOption];

const filterByState = (service: AddOnService, selectedFilterId: string): boolean => {
    switch (selectedFilterId) {
        case ActiveFilterOption.id:
            return service.active;
        case InactiveFilterOption.id:
            return !service.active;
        default:
            return true;
    }
};

const match = (text: string, service: AddOnService): boolean => {
    if (isEmpty(text)) {
        return true;
    } else {
        const tokens = text.split(' ').map(token => token.toLowerCase());
        return tokens.every(token => service.name.toLowerCase().indexOf(token) > -1);
    }
};

export const AddOnServices: FunctionComponent = () => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const [width] = useState(window.innerWidth);
    const serviceStatus = useSelector<RootState, AddOnServicesStatus>(state => state.addOnServices.status);
    const services = useSelector<RootState, Array<AddOnService>>(state => state.addOnServices.services);
    const isSm = width < 415;
    const [loading, setLoading] = useState(true);
    const [selectedFilterId, setSelectedFilterId] = useState(ActiveFilterOption.id);
    const [searchText, setSearchText] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [editedService, setEditedService] = useState<AddOnService | null>(null);
    const [deleteService, setDeleteService] = useState<AddOnService | null>(null);

    const filteredServices = services
        .filter(service => filterByState(service, selectedFilterId))
        .filter(service => match(searchText, service));

    useEffect(() => {
        dispatch(fetchThunk(apiClientWrapper));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useNonInitialEffect(() => {
        switch (serviceStatus) {
            case AddOnServicesStatus.FetchSuccess:
                setLoading(false);
                break;
            case AddOnServicesStatus.Error:
                setLoading(false);
        }
    }, [serviceStatus]);

    const hideSuccess = () => {
        setShowSuccess(false);
        setEditedService(null);
    };

    const success = (
        <div className={styles.containerMainSuccess}>
            <div className={styles.containerSuccess}>
                <FontAwesomeIcon
                    className={styles.checkIcon}
                    icon={faCheck}
                />
                <FontAwesomeIcon
                    className={styles.checkIconSm}
                    icon={faCheckCircle}
                />
                <h2 className={styles.titleSuccess}>{'Good Job!'}</h2>
                {editedService && (
                    <div className={styles.descriptionSuccess}>
                        <p className={styles.labelServiceName}>
                            <strong>{editedService.name} </strong>
                        </p>
                        <p className={styles.labelDescriptionSuccess}>was added successfully.</p>
                    </div>
                )}
            </div>
            <div className={styles.buttons}>
                <Button
                    id="services_success_button_go_to_services_addon"
                    label="Go to Add-Ons"
                    onClick={() => hideSuccess()}
                    className={clsx(styles.button, styles.buttonServices)}
                />
            </div>
        </div>
    );

    const table = (
        <>
            <Search onSearch={setSearchText} />
            <Filters
                selectedId={selectedFilterId}
                options={FilterOptions}
                onChange={setSelectedFilterId}
            />
            <Table
                showStatus={selectedFilterId === AllFilterOption.id}
                services={filteredServices}
                onEdit={setEditedService}
                onDelete={setDeleteService}
                onChangeStatus={service => dispatch(changeStateThunk(apiClientWrapper, service.id!, service.active))}
            />
        </>
    );
    return (
        <>
            {showSuccess && success}
            {!editedService && !showSuccess && (
                <div className={styles.container}>
                    <ServicesAddonsHeader onAdd={() => setEditedService(createEmptyAddOnService(uuid))} />
                    {loading && <Spinner />}
                    {!loading && services.length > 0 && table}
                    {!loading && services.length === 0 && (
                        <ServicesAddonsEmpty onAdd={() => setEditedService(createEmptyAddOnService(uuid))} />
                    )}
                </div>
            )}
            {editedService && !showSuccess && (
                /*        <OverFullWindow
          hideHeader={true}
          title={editedService.id ? "Editing Add-On Service" : "Adding Add-On Service"}
          onClose={() => setEditedService(null)}
        >*/
                <AddOnServiceForm
                    service={editedService}
                    onClose={() => {
                        setEditedService(null);
                    }}
                    onSaved={savedService => {
                        setEditedService(savedService);
                        setShowSuccess(true);
                    }}
                />
                /*
        </OverFullWindow>
*/
            )}
            {deleteService && (
                <ConfirmDialog
                    open={true}
                    title={<span>Delete Add-On Service</span>}
                    question={
                        <span>
                            Are you sure want to delete
                            <br />
                            <strong>{deleteService!.name}</strong> add-on service?
                        </span>
                    }
                    cancelButtonLabel="Cancel"
                    confirmButtonLabel="Confirm"
                    onCancel={() => setDeleteService(null)}
                    onConfirm={() => {
                        dispatch(deleteThunk(apiClientWrapper, deleteService!.id!));
                        setDeleteService(null);
                    }}
                />
            )}
        </>
    );
};

export default AddOnServices;
